import * as React from "react"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  // Design spec: 2px 8px padding, 4px radius, weight 600. Shape treatment
  // per status is applied via the `status` prop below and by the
  // status-treatment CSS in index.css so pages can opt in without a
  // codebase-wide sweep.
  "inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

/**
 * Normalize an arbitrary status label to a shape treatment. The mapping is
 * generous (case-insensitive, matches common synonyms) so a page can pass
 * the raw status string it already has without extra plumbing.
 *
 * Approved         -> filled, no border (already the default; we mark it
 *                     explicitly via data-status so CSS can hook later).
 * Filing           -> transparent bg, 1px border in the badge color.
 * Review / In Review -> filled bg + 2px left accent border.
 * Anything else    -> outlined treatment matching Filing.
 */
function shapeForStatus(status) {
  if (!status) return null;
  const s = String(status).trim().toLowerCase();
  if (s === "approved") return "approved";
  if (s === "filing") return "filing";
  if (s === "review" || s === "in review" || s === "in-review") return "review";
  return "other";
}

function Badge({ className, variant, status, ...props }) {
  const shape = shapeForStatus(status);
  return (
    <div
      data-status={shape || undefined}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants }
